import React, { useCallback, useEffect, useState } from 'react'
import { Building2, Loader2, MoreHorizontal, Plus } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  organizationService,
  type OrganizationInvite,
  type OrganizationMember,
} from '@/services/organizationService'
import { SettingsCard } from './SettingsCard'

// An owner can invite and manage the team; shown as Admin
const ROLE_LABEL: Record<string, string> = { owner: 'Admin', admin: 'Admin', member: 'Member' }
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const initials = (n: string) =>
  n
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/** Settings > Team: who has access to this company, and invitations not yet accepted */
export const TeamTab: React.FC = () => {
  const { toast } = useToast()
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [invites, setInvites] = useState<OrganizationInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'owner'>('member')
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await organizationService.getMembers()
      setMembers(data.members)
      setInvites(data.invites.filter((i) => i.status === 'pending'))
    } catch {
      toast({ title: "Couldn't load your team", variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }, [toast])
  useEffect(() => {
    load()
  }, [load])

  const invite = async () => {
    setSending(true)
    try {
      await organizationService.inviteMember(email.trim(), role)
      toast({ title: 'Invitation sent', description: `${email.trim()} gets an email to join.` })
      setEmail('')
      setInviteOpen(false)
      load()
    } catch (err: any) {
      toast({
        title: "Couldn't invite",
        description: err?.response?.data?.error ?? 'Try again.',
        variant: 'destructive',
      })
    } finally {
      setSending(false)
    }
  }

  const revoke = async (i: OrganizationInvite) => {
    try {
      await organizationService.revokeInvite(i.id)
      toast({ title: 'Invitation cancelled' })
      load()
    } catch {
      toast({ title: "Couldn't cancel the invitation", variant: 'destructive' })
    }
  }

  return (
    <div className="max-w-4xl space-y-4">
      <section className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
        {org?.logo ? (
          <img src={org.logo} alt="" className="h-12 w-12 rounded-xl object-cover" />
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F1F5F0] text-foreground">
            <Building2 className="h-6 w-6" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Your company</p>
          <p className="truncate text-foreground">{org?.name ?? '—'}</p>
          <p className="text-xs text-muted-foreground">
            {members.length} team member{members.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#C0FD72] px-5 font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
        >
          <Plus className="h-4 w-4" /> Invite member
        </button>
      </section>

      <SettingsCard title="Team members" subtitle="Manage who has access to this company.">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Role</th>
                  <th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((m) => (
                  <tr key={m.id}>
                    <td className="py-3">
                      <span className="flex items-center gap-2.5">
                        {m.avatar ? (
                          <img
                            src={m.avatar}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#DBEAFE] text-xs font-bold text-[#1D4ED8]">
                            {initials(m.name || m.email)}
                          </span>
                        )}
                        <span className="font-medium text-foreground">{m.name || '—'}</span>
                      </span>
                    </td>
                    <td className="py-3 text-foreground">{m.email}</td>
                    <td className="py-3 text-foreground">
                      {ROLE_LABEL[m.org_role ?? 'member'] ?? m.org_role}
                    </td>
                    <td className="py-3">
                      <span className="inline-flex items-center gap-1.5 text-[#16A34A]">
                        <span className="h-2 w-2 rounded-full bg-[#22C55E]" /> Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>

      <SettingsCard
        title="Pending invitations"
        subtitle="Invitations that haven't been accepted yet."
      >
        {invites.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Role</th>
                  <th className="pb-2 font-medium">Sent</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td className="py-3 text-foreground">{i.email}</td>
                    <td className="py-3 text-foreground">{ROLE_LABEL[i.role] ?? i.role}</td>
                    <td className="py-3 text-foreground">{day(i.createdAt)}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center gap-1.5 text-[#D97706]">
                        <span className="h-2 w-2 rounded-full bg-[#F59E0B]" /> Pending
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          aria-label="Invitation actions"
                          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => revoke(i)}>
                            Cancel invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No pending invitations.</p>
        )}
      </SettingsCard>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="border-border bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              They'll get an email to join {org?.name ?? 'your company'}.
            </DialogDescription>
          </DialogHeader>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            aria-label="Email"
            className="h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground outline-none focus:border-[#84CC16]"
          />
          <div className="flex gap-2">
            {(['member', 'owner'] as const).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={role === r}
                onClick={() => setRole(r)}
                className={`h-10 flex-1 rounded-lg border text-sm font-semibold ${role === r ? 'border-[#9BD85A] bg-[#E8F9D2] text-[#1F3D05]' : 'border-border text-foreground'}`}
              >
                {ROLE_LABEL[r]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={invite}
            disabled={sending || !/^\S+@\S+\.\S+$/.test(email.trim())}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send invitation
          </button>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default TeamTab
